import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AccuracySparkline } from '../components/AccuracySparkline';
import { Card } from '../components/Card';
import { StatBox } from '../components/StatBox';
import { StreakIndicator } from '../components/StreakIndicator';
import { api } from '../lib/api';
import { useUser } from '../lib/auth';
import { kickoffLabel } from '../lib/utils';

export function Profile() {
  const user = useUser();
  const preds = useQuery({ queryKey: ['predictions', 'me'], queryFn: () => api.predictionsMe(200) });
  const statsQ = useQuery({ queryKey: ['profile', 'stats'], queryFn: () => api.profileStats() });
  const creditQ = useQuery({ queryKey: ['credit'], queryFn: () => api.myCredit() });

  const acc = statsQ.data?.markets.oneXTwo.accuracyPct ?? null;
  const stats = useMemo(() => {
    const list = preds.data?.predictions ?? [];
    return { total: list.length };
  }, [preds.data]);

  if (user.isLoading || !user.data) {
    return (
      <div className="mx-auto max-w-md px-4 pt-10">
        <div className="h-32 animate-pulse rounded-xl bg-bg-card border border-border-base" />
      </div>
    );
  }

  const u = user.data;

  return (
    <div className="mx-auto max-w-md px-4 pt-4 pb-24 space-y-4">
      <header className="flex items-center gap-4">
        {u.photoUrl ? (
          <img src={u.photoUrl} alt="" className="h-16 w-16 rounded-full border-2 border-trophy-gold" />
        ) : (
          <div className="h-16 w-16 rounded-full bg-bg-elevated border-2 border-border-base flex items-center justify-center font-display text-2xl">
            {u.firstName.charAt(0)}
          </div>
        )}
        <div>
          <div className="font-display text-2xl uppercase">{u.firstName}</div>
          {u.telegramUsername && (
            <div className="font-mono text-xs text-text-faint">@{u.telegramUsername}</div>
          )}
          <div className="flex flex-wrap gap-1.5 mt-1">
            {u.hasBracketPremium ? (
              <span className="inline-block rounded-md bg-trophy-gold/20 border border-trophy-gold/40 px-2 py-0.5 font-display-alt text-[10px] uppercase tracking-widest text-trophy-gold">
                Bracket Premium
              </span>
            ) : null}
            {u.premiumUntil && u.premiumUntil > Math.floor(Date.now() / 1000) ? (
              <span className="inline-block rounded-md bg-premium/20 border border-premium/40 px-2 py-0.5 font-display-alt text-[10px] uppercase tracking-widest text-premium">
                Premium
              </span>
            ) : null}
          </div>
        </div>
      </header>

      <div className="flex gap-3">
        <StatBox label="Lifetime pts" value={u.totalPoints.toLocaleString()} accent="gold" />
        <StatBox label="Bracket pts" value={u.bracketPoints.toLocaleString()} accent="gold" />
      </div>
      <div className="flex gap-3">
        <StatBox label="Predictions" value={u.predictionsCount.toLocaleString()} />
        <StatBox label="1X2 accuracy" value={acc !== null ? `${acc}%` : '—'} accent="pitch" />
      </div>

      {/* In-app Stars credit */}
      {creditQ.data && creditQ.data.availableCreditStars > 0 && (
        <Card>
          <div className="flex items-baseline justify-between">
            <div>
              <div className="font-display-alt text-xs uppercase tracking-widest text-text-faint">
                Stars credit
              </div>
              <div className="font-mono text-2xl text-trophy-gold mt-1">
                {creditQ.data.availableCreditStars} ⭐
              </div>
            </div>
            <div className="text-xs text-text-dim text-right max-w-[60%]">
              In-app credit from refunds. Spend on any PunditXI Stars item — can't move back to your
              Telegram Stars balance (Telegram limitation).
            </div>
          </div>
        </Card>
      )}

      {/* Premium-only accuracy trend (component self-hides on 402) */}
      {u.premiumUntil && u.premiumUntil > Math.floor(Date.now() / 1000) && <AccuracySparkline />}

      {/* Per-market accuracy */}
      {statsQ.data && statsQ.data.totalScored > 0 && (
        <Card>
          <div className="font-display-alt text-xs uppercase tracking-widest text-text-faint mb-2">
            Per-market accuracy
          </div>
          <div className="space-y-1.5 text-sm">
            <MarketRow label="1X2" m={statsQ.data.markets.oneXTwo} />
            <MarketRow label="BTTS" m={statsQ.data.markets.btts} />
            <MarketRow label="Correct Score" m={statsQ.data.markets.correctScore} />
            <MarketRow label="Knockout winner" m={statsQ.data.markets.knockoutWinner} />
          </div>
        </Card>
      )}

      {/* Per-competition table */}
      {statsQ.data && statsQ.data.perCompetition.length > 0 && (
        <Card>
          <div className="font-display-alt text-xs uppercase tracking-widest text-text-faint mb-2">
            By competition
          </div>
          <div className="space-y-1.5 text-sm">
            {statsQ.data.perCompetition.map((c) => (
              <div key={c.id} className="flex items-center justify-between">
                <span className="font-chunky text-text-base">{c.shortName}</span>
                <span className="font-mono text-text-dim text-xs">
                  {c.correct}/{c.picks}
                  {c.accuracyPct !== null ? ` · ${c.accuracyPct}%` : ''}
                </span>
                <span className="font-mono text-trophy-gold">{c.points} pts</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card>
        <div className="flex items-center justify-between">
          <span className="font-display-alt text-xs uppercase tracking-widest text-text-faint">
            Streak
          </span>
          <StreakIndicator streak={u.currentStreak} />
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="font-display-alt text-xs uppercase tracking-widest text-text-faint">
            Longest streak
          </span>
          <span className="font-mono text-trophy-gold">{u.longestStreak}</span>
        </div>
      </Card>

      <section>
        <h2 className="font-display text-xl uppercase tracking-wide mb-2">Recent predictions</h2>
        {preds.isLoading && (
          <div className="space-y-2 animate-pulse">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-12 rounded-xl bg-bg-card border border-border-base" />
            ))}
          </div>
        )}
        {preds.data && preds.data.predictions.length === 0 && (
          <Card>
            <div className="text-text-dim text-sm">No predictions yet. Open Today and pick a match.</div>
          </Card>
        )}
        <div className="space-y-1.5">
          {preds.data?.predictions.slice(0, 20).map((p) => (
            <Link
              key={p.id}
              to={`/fixture/${p.fixtureId}`}
              className="flex items-center justify-between rounded-md border border-border-base bg-bg-card px-3 py-2 active:scale-[0.99]"
            >
              <div>
                <div className="font-chunky text-sm">Fixture #{p.fixtureId}</div>
                <div className="font-display-alt text-[10px] uppercase tracking-widest text-text-faint mt-0.5">
                  {kickoffLabel(p.createdAt)}
                </div>
              </div>
              <div className="text-right">
                {p.pointsAwarded !== null ? (
                  <span className="font-mono text-trophy-gold">+{p.pointsAwarded}</span>
                ) : (
                  <span className="font-display-alt text-[10px] uppercase tracking-widest text-text-faint">
                    Pending
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
      </section>

      <Link
        to="/settings"
        className="block text-center font-display-alt text-xs uppercase tracking-widest text-text-dim pt-2"
      >
        Settings →
      </Link>
    </div>
  );
}

function MarketRow({
  label,
  m,
}: {
  label: string;
  m: { picked: number; correct: number; accuracyPct: number | null };
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-text-dim">{label}</span>
      <span className="font-mono text-text-base text-xs">
        {m.correct}/{m.picked}
      </span>
      <span className="font-mono text-trophy-gold w-12 text-right">
        {m.accuracyPct !== null ? `${m.accuracyPct}%` : '—'}
      </span>
    </div>
  );
}
